import { useState } from "react";
import { useTranslation } from "react-i18next";
import Modal from "../../../../components/base/Modal";
import { Badge, Button, confirmDelete, notifyError, type Tone } from "../../../../components/ui";
import { usePatrolsStore } from "../../patrolsStore";
import { useBasesLocacionesStore } from "../../basesStore";
import { apiErrorMessage } from "../../services/basesApi";
import { SHIFTS, type PatrolSchedule, type PersonRef } from "../../services/patrolsApi";
import { AvatarStack, FilterSelect, FormField, Kpi, LoadBlock, PersonAvatar, SiteTree, Toolbar, inputCls } from "../../components/console";
import { treeMatch, useRondinesStatus, useRouteTree } from "./shared";

// Programación por turnos: ruta, turno, días, ventana, frecuencia, horarios
// resultantes y guardias asignados (avatares de iniciales).

const WEEKDAYS = ["lun", "mar", "mie", "jue", "vie", "sab", "dom"] as const;
const SHIFT_TONES: Record<string, Tone> = { matutino: "warning", vespertino: "info", nocturno: "primary" };
const SHIFT_ICONS: Record<string, string> = { matutino: "ri-sun-line", vespertino: "ri-sun-foggy-line", nocturno: "ri-moon-line" };
const SHIFT_DEFAULTS: Record<string, [string, string]> = { matutino: ["07:00", "15:00"], vespertino: ["15:00", "23:00"], nocturno: ["23:00", "07:00"] };

type Draft = Pick<PatrolSchedule, "route_id" | "name" | "shift" | "days" | "startTime" | "endTime" | "everyMinutes" | "toleranceMinutes" | "guards" | "status">;

export default function RondinesSchedules() {
  const { t } = useTranslation("bases");
  const { routes, schedules, saveSchedule, removeSchedule } = usePatrolsStore();
  const staff = useBasesLocacionesStore((s) => s.staff);
  const { status, error, retry } = useRondinesStatus();
  const tree = useRouteTree();
  const [sel, setSel] = useState<string | null>(null);
  const [shift, setShift] = useState<string>("");
  const [editing, setEditing] = useState<{ id: string | null; draft: Draft } | null>(null);
  const [saving, setSaving] = useState(false);

  const list = schedules.filter((s) => treeMatch(sel, s) && (!shift || s.shift === shift));
  const guardsAll = new Map<string, PersonRef>();
  schedules.forEach((s) => s.guards.forEach((g) => guardsAll.set(g.id || g.name, g)));

  const editingRoute = routes.find((r) => r.id === editing?.draft.route_id);
  const candidates = staff.filter((p) => p.role === "seguridad" && (!editingRoute || p.base_id === editingRoute.base_id));

  const patch = (p: Partial<Draft>) => setEditing((e) => (e ? { ...e, draft: { ...e.draft, ...p } } : e));
  const newDraft = (): Draft => ({ route_id: sel?.startsWith("route:") ? sel.slice(6) : routes[0]?.id ?? "", name: "", shift: "nocturno", days: [...WEEKDAYS], startTime: "23:00", endTime: "07:00", everyMinutes: 120, toleranceMinutes: 10, guards: [], status: "active" });

  const save = async () => {
    if (!editing?.draft.route_id) return void notifyError(t("rondines.schedules.required"));
    setSaving(true);
    try {
      await saveSchedule(editing.id, editing.draft);
      setEditing(null);
    } catch (err) {
      notifyError(apiErrorMessage(err, t("common.saveError")));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (s: PatrolSchedule) => {
    if (!(await confirmDelete({ title: t("rondines.schedules.deleteTitle", { name: s.name }), cancelText: t("common.cancel"), confirmText: t("common.delete") }))) return;
    try {
      await removeSchedule(s.id);
    } catch (err) {
      notifyError(apiErrorMessage(err, t("common.saveError")));
    }
  };

  return (
    <div className="flex h-full w-full overflow-hidden">
      <SiteTree title={t("rondines.treeTitle")} allLabel={t("rondines.allRoutes")} nodes={tree} selected={sel} onSelect={setSel} />
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <Toolbar>
          <div className="mr-2 flex items-center gap-2">
            <i className="ri-calendar-schedule-line text-lg text-primary-500" />
            <h1 className="text-sm font-semibold text-content">{t("rondines.schedules.title")}</h1>
          </div>
          <FilterSelect label={t("fields.shift")} value={shift} onChange={setShift} options={SHIFTS.map((s) => ({ value: s, label: t(`shifts.${s}`) }))} />
          <div className="ml-auto">
            <Button size="sm" icon={<i className="ri-add-line" />} disabled={!routes.length} onClick={() => setEditing({ id: null, draft: newDraft() })}>
              {t("rondines.schedules.new")}
            </Button>
          </div>
        </Toolbar>
        <div className="flex-1 overflow-y-auto p-4">
          <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-4">
            <Kpi icon="ri-calendar-schedule-line" label={t("rondines.schedules.title")} value={schedules.length} />
            <Kpi icon="ri-repeat-line" label={t("rondines.schedules.roundsPerDay")} value={schedules.filter((s) => s.status === "active").reduce((a, s) => a + s.times.length, 0)} tone="info" />
            <Kpi icon="ri-shield-user-line" label={t("rondines.schedules.guards")} value={guardsAll.size} tone="primary" hint={<AvatarStack names={[...guardsAll.values()].map((g) => g.name)} />} />
            <Kpi icon="ri-moon-line" label={t("shifts.nocturno")} value={schedules.filter((s) => s.shift === "nocturno").length} tone="warning" />
          </div>
          <LoadBlock status={status} error={error} onRetry={retry} empty={schedules.length === 0} emptyIcon="ri-calendar-schedule-line" emptyTitle={t("rondines.schedules.emptyTitle")} emptyText={t("rondines.schedules.emptyText")}>
            <div className="overflow-x-auto rounded-xl border border-line bg-card">
              <table className="w-full min-w-[860px] text-sm">
                <thead className="bg-subtle text-left text-xs uppercase tracking-wide text-content-secondary">
                  <tr>
                    <th className="px-3 py-2">{t("fields.route")}</th>
                    <th className="px-3 py-2">{t("fields.shift")}</th>
                    <th className="px-3 py-2">{t("fields.days")}</th>
                    <th className="px-3 py-2">{t("rondines.schedules.window")}</th>
                    <th className="px-3 py-2">{t("rondines.schedules.times")}</th>
                    <th className="px-3 py-2">{t("rondines.schedules.guards")}</th>
                    <th className="px-3 py-2">{t("fields.status")}</th>
                    <th className="px-3 py-2" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-line">
                  {list.map((s) => (
                    <tr key={s.id} className="hover:bg-subtle">
                      <td className="px-3 py-2 font-medium text-content">{s.routeName ?? routes.find((r) => r.id === s.route_id)?.name}</td>
                      <td className="px-3 py-2">
                        <Badge size="sm" tone={SHIFT_TONES[s.shift] ?? "neutral"}>
                          <i className={SHIFT_ICONS[s.shift]} /> {t(`shifts.${s.shift}`)}
                        </Badge>
                      </td>
                      <td className="px-3 py-2 text-xs text-content-secondary">{s.days.length === 7 ? t("rondines.schedules.everyDay") : s.days.map((d) => t(`weekdays.${d}`)).join(" ")}</td>
                      <td className="px-3 py-2 tabular-nums text-content-secondary">
                        {s.startTime}–{s.endTime} · {t("rondines.schedules.every", { n: s.everyMinutes })}
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex flex-wrap gap-1">
                          {s.times.slice(0, 6).map((h) => (
                            <span key={h} className="rounded bg-subtle px-1.5 py-0.5 font-mono text-[11px] text-content-secondary">
                              {h}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        <span className="flex items-center gap-2">
                          <AvatarStack names={s.guards.map((g) => g.name)} />
                          <span className="hidden truncate text-xs text-content-secondary xl:inline">{s.guards.map((g) => g.name.split(" ")[0]).join(", ")}</span>
                        </span>
                      </td>
                      <td className="px-3 py-2">
                        <Badge size="sm" tone={s.status === "active" ? "success" : "neutral"} dot>
                          {t(`rondines.scheduleStatus.${s.status}`)}
                        </Badge>
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 text-right">
                        <Button size="xs" variant="ghost" iconOnly icon={<i className="ri-edit-line" />} aria-label={t("common.edit")} onClick={() => setEditing({ id: s.id, draft: { route_id: s.route_id, name: s.name, shift: s.shift, days: s.days, startTime: s.startTime, endTime: s.endTime, everyMinutes: s.everyMinutes, toleranceMinutes: s.toleranceMinutes, guards: s.guards, status: s.status } })} />
                        <Button size="xs" variant="ghost" iconOnly icon={<i className="ri-delete-bin-line" />} aria-label={t("common.delete")} onClick={() => void remove(s)} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </LoadBlock>
        </div>
      </div>

      {editing ? (
        <Modal
          title={editing.id ? t("rondines.schedules.edit") : t("rondines.schedules.new")}
          onClose={() => setEditing(null)}
          content={
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <FormField label={t("fields.route")} className="sm:col-span-2">
                <select className={inputCls} value={editing.draft.route_id} onChange={(e) => patch({ route_id: e.target.value, guards: [] })}>
                  {routes.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.name}
                    </option>
                  ))}
                </select>
              </FormField>
              <FormField label={t("fields.shift")}>
                <select className={inputCls} value={editing.draft.shift} onChange={(e) => { const s = e.target.value as Draft["shift"]; patch({ shift: s, startTime: SHIFT_DEFAULTS[s][0], endTime: SHIFT_DEFAULTS[s][1] }); }}>
                  {SHIFTS.map((s) => (
                    <option key={s} value={s}>
                      {t(`shifts.${s}`)}
                    </option>
                  ))}
                </select>
              </FormField>
              <FormField label={t("fields.openTime")}>
                <input className={inputCls} type="time" value={editing.draft.startTime} onChange={(e) => patch({ startTime: e.target.value })} />
              </FormField>
              <FormField label={t("fields.closeTime")}>
                <input className={inputCls} type="time" value={editing.draft.endTime} onChange={(e) => patch({ endTime: e.target.value })} />
              </FormField>
              <FormField label={t("rondines.schedules.everyLabel")}>
                <input className={inputCls} type="number" min={15} step={15} value={editing.draft.everyMinutes} onChange={(e) => patch({ everyMinutes: Number(e.target.value || 60) })} />
              </FormField>
              <div className="sm:col-span-3">
                <span className="mb-1 block text-xs font-medium text-content-secondary">{t("fields.days")}</span>
                <div className="flex flex-wrap gap-1.5">
                  {WEEKDAYS.map((d) => {
                    const on = editing.draft.days.includes(d);
                    return (
                      <button key={d} type="button" onClick={() => patch({ days: on ? editing.draft.days.filter((x) => x !== d) : [...editing.draft.days, d] })} className={`rounded-lg border px-2.5 py-1 text-xs ${on ? "border-primary-500 bg-primary-500/15 text-primary-500" : "border-line text-content-secondary"}`}>
                        {t(`weekdays.${d}`)}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="sm:col-span-3">
                <span className="mb-1 block text-xs font-medium text-content-secondary">{t("rondines.schedules.guards")}</span>
                {candidates.length === 0 ? <p className="text-xs text-content-muted">{t("rondines.schedules.noGuards")}</p> : null}
                <div className="flex flex-wrap gap-2">
                  {candidates.map((p) => {
                    const on = editing.draft.guards.some((g) => g.id === p.id);
                    return (
                      <button key={p.id} type="button" onClick={() => patch({ guards: on ? editing.draft.guards.filter((g) => g.id !== p.id) : [...editing.draft.guards, { id: p.id, name: p.name }] })} className={`flex items-center gap-2 rounded-full border py-1 pl-1 pr-3 text-xs ${on ? "border-primary-500 bg-primary-500/15 text-primary-500" : "border-line text-content-secondary"}`}>
                        <PersonAvatar name={p.name} size="sm" /> {p.name}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="flex justify-end gap-2 border-t border-line pt-4 sm:col-span-3">
                <Button variant="secondary" onClick={() => setEditing(null)}>
                  {t("common.cancel")}
                </Button>
                <Button loading={saving} onClick={() => void save()} icon={<i className="ri-save-line" />}>
                  {t("common.save")}
                </Button>
              </div>
            </div>
          }
        />
      ) : null}
    </div>
  );
}
